
import { useState } from "react";
import { isWithinInterval, parseISO, startOfDay, endOfDay } from "date-fns";

export interface FilterableRecord {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  [key: string]: any; // Allow any other properties
}

export const useDataFilters = <T extends FilterableRecord>(
  data: T[], 
  dateField: string = 'created_at' // Allow customizing which date field to use
) => {
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState<string>("");
  
  // Filter data by date range and search term
  const filteredData = data.filter(item => {
    // Date range filter
    let dateMatches = true;
    if (startDate || endDate) {
      // Make sure the item has the specified date field
      if (item[dateField]) {
        const itemDate = parseISO(item[dateField]);
        
        if (startDate && endDate) {
          dateMatches = isWithinInterval(itemDate, {
            start: startOfDay(startDate),
            end: endOfDay(endDate)
          });
        } else if (startDate) {
          dateMatches = itemDate >= startOfDay(startDate);
        } else if (endDate) {
          dateMatches = itemDate <= endOfDay(endDate);
        }
      }
    }
    
    // Search filter (name, first name or email)
    let searchMatches = true;
    if (searchTerm.trim() !== "") {
      const searchLower = searchTerm.toLowerCase();
      searchMatches = 
        item.first_name.toLowerCase().includes(searchLower) || 
        item.last_name.toLowerCase().includes(searchLower) || 
        item.email.toLowerCase().includes(searchLower);
    }
    
    return dateMatches && searchMatches;
  });

  const handleStartDateChange = (date: Date | undefined) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date: Date | undefined) => {
    setEndDate(date);
  };
  
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  return {
    startDate,
    endDate,
    searchTerm,
    filteredData,
    handleStartDateChange,
    handleEndDateChange,
    handleSearchChange
  };
};
